'use client';

import { FC } from 'react';

import { User } from '@ravago/shared/authentication';
import { UOM } from '@ravago/shared/page-data/models/config';
import { RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';

import DocumentListItemBody from './document-list-item-body';

interface Props {
   document: RelatedDocument;
   locale: string;
   hasSharePermission?: boolean;
   sharedDocument: boolean;
   hasPublicTdsDownloadPermission: boolean;
   onDocumentDownload: (document: RelatedDocument, selectedLanguageId?: string, selectedUOMId?: string) => void;
   onShareChecked: (document: RelatedDocument) => void;
   onLanguageChange?: (lan: DownloadOption | undefined) => void;
   onUOMChange?: (uom: DownloadOption | undefined) => void;
   selectedLanguage?: DownloadOption;
   selectedUOM?: DownloadOption;
   availableLanguages: DownloadOption[];
   channelUOMs: Array<UOM>;
   user?: User;
   detailPageName?: string;
   ulProspectorId: string | undefined;
   language: string;
   publicShowTdsUom: boolean;
   showTdsUom: boolean;
   canDownloadDocumentsAnonymously?: boolean;
}

const DocumentListItem: FC<Props> = ({
   document,
   user,
   hasPublicTdsDownloadPermission,
   hasSharePermission,
   sharedDocument,
   language,
   selectedLanguage,
   selectedUOM,
   onDocumentDownload,
   onLanguageChange,
   onUOMChange,
   availableLanguages,
   onShareChecked,
   ulProspectorId,
   detailPageName,
   locale,
   channelUOMs,
   publicShowTdsUom,
   showTdsUom,
   canDownloadDocumentsAnonymously
}) => {
   const isAuthenticated = !!user || !!canDownloadDocumentsAnonymously;

   return (
      <Box direction="column">
         <BreakpointSwitch includeViews={['desktop']}>
            <DocumentListItemBody
               locale={locale}
               onShareChecked={onShareChecked}
               sharedDocument={sharedDocument}
               document={document}
               superCondensedView={false}
               ulProspectorId={ulProspectorId}
               condensedView={false}
               hasPublicTdsDownloadPermission={hasPublicTdsDownloadPermission}
               onLanguageChange={onLanguageChange}
               onUOMChange={onUOMChange}
               onDocumentDownload={() => onDocumentDownload(document, selectedLanguage?.id, selectedUOM?.id)}
               availableLanguages={availableLanguages}
               channelUOMs={channelUOMs}
               detailPageName={detailPageName}
               publicShowTdsUom={publicShowTdsUom}
               showTdsUom={showTdsUom}
               isAuthenticated={isAuthenticated}
               hasSharePermission={hasSharePermission}
               selectedUOM={selectedUOM}
               selectedLanguage={selectedLanguage}
               language={language}
               user={user}
            />
         </BreakpointSwitch>
         <BreakpointSwitch includeViews={['tab-port', 'tab-land']}>
            <DocumentListItemBody
               locale={locale}
               document={document}
               onShareChecked={onShareChecked}
               superCondensedView={false}
               condensedView
               ulProspectorId={ulProspectorId}
               sharedDocument={sharedDocument}
               hasPublicTdsDownloadPermission={hasPublicTdsDownloadPermission}
               onLanguageChange={onLanguageChange}
               onUOMChange={onUOMChange}
               onDocumentDownload={() => onDocumentDownload(document, selectedLanguage?.id, selectedUOM?.id)}
               availableLanguages={availableLanguages}
               channelUOMs={channelUOMs}
               detailPageName={detailPageName}
               publicShowTdsUom={publicShowTdsUom}
               showTdsUom={showTdsUom}
               isAuthenticated={isAuthenticated}
               hasSharePermission={hasSharePermission}
               selectedUOM={selectedUOM}
               selectedLanguage={selectedLanguage}
               language={language}
               user={user}
            />
         </BreakpointSwitch>
         <BreakpointSwitch includeViews={['phone']}>
            <DocumentListItemBody
               locale={locale}
               document={document}
               superCondensedView
               onShareChecked={onShareChecked}
               sharedDocument={sharedDocument}
               condensedView
               ulProspectorId={ulProspectorId}
               hasPublicTdsDownloadPermission={hasPublicTdsDownloadPermission}
               onLanguageChange={onLanguageChange}
               onUOMChange={onUOMChange}
               onDocumentDownload={() => onDocumentDownload(document, selectedLanguage?.id, selectedUOM?.id)}
               availableLanguages={availableLanguages}
               channelUOMs={channelUOMs}
               detailPageName={detailPageName}
               publicShowTdsUom={publicShowTdsUom}
               showTdsUom={showTdsUom}
               isAuthenticated={isAuthenticated}
               hasSharePermission={hasSharePermission}
               selectedUOM={selectedUOM}
               selectedLanguage={selectedLanguage}
               language={language}
               user={user}
            />
         </BreakpointSwitch>
      </Box>
   );
};

export default DocumentListItem;
